// Contrato del bloque SEGMENTOS de `loan-offerings-business`, escrito a mano a partir de su
// `openapi/public.yaml`. Un cliente entra si cumple al menos uno de los cohorts o está en la lista.
import type { CustomerFacts, DefinitionLogEvent } from './cohorts.contract';

/** `ARCHIVED` sale del motor y de los selectores; el nombre sigue resolviendo. */
export type SegmentStatusWire = 'ACTIVE' | 'ARCHIVED';

export interface SegmentResponse {
    segmentId: string;
    name: string;
    description: string | null;
    /** Slot de la paleta pastel (`slate`, `sky`, `amber`, …), no un color literal. */
    colorSlot: string;
    /** Cohorts del segmento. Entre ellos la relación es O: basta cumplir uno. */
    cohortIds: string[];
    /** Lista manual por `retailCustomerId`: entran aunque no cumplan ningún cohort. */
    memberIds: string[];
    /** Solo el catch-all (`SEG-STD`). No acepta cohorts ni individuos; solo se renombra. */
    isDefault: boolean;
    status: SegmentStatusWire;
    version: number;
    /** Clientes del último recálculo. `null` = sin calcular. */
    clientCount: number | null;
    /** Porcentaje de los acreditados del tenant, de 0 a 100. `null` = sin calcular. */
    pctTotal: number | null;
    countedAt: string | null;
    /** Nunca se contó, se editó después del último recálculo o uno de sus cohorts quedó pendiente. */
    countPending: boolean;
    createdBy: string;
    updatedBy: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateSegmentRequest {
    name: string;
    description?: string | null;
    colorSlot: string;
    /** Cohorts ACTIVE, máximo 20. */
    cohortIds?: string[];
    memberIds?: string[];
}

/** Parche parcial: solo los campos presentes cambian. Cada lista se reemplaza entera. */
export interface UpdateSegmentRequest {
    name?: string;
    description?: string | null;
    colorSlot?: string;
    cohortIds?: string[];
    memberIds?: string[];
    /** Motivo del cambio; queda en la bitácora. */
    reason?: string;
}

export interface ChangeSegmentStatusRequest {
    status: SegmentStatusWire;
    reason?: string;
}

export interface SegmentUsagePolicyRef {
    policyId: string;
    name: string;
    status: string;
}

export interface SegmentUsagePromotionRef {
    promotionId: string;
    name: string;
    status: string;
}

/** Políticas y promociones que apuntan al segmento, en todos sus estados. */
export interface SegmentUsageResponse {
    segmentId: string;
    policies: SegmentUsagePolicyRef[];
    promotions: SegmentUsagePromotionRef[];
}

export interface TestCustomerSegmentsRequest {
    retailCustomerId: string;
}

/** Por qué entra: por la lista manual o por un cohort. `null` cuando no entra. */
export type SegmentMembershipReason = 'MEMBER' | 'COHORT';

export interface CustomerSegmentTrace {
    segmentId: string;
    name: string;
    matched: boolean;
    reason: SegmentMembershipReason | null;
    matchedCohortIds: string[];
}

export interface TestCustomerSegmentsResponse {
    retailCustomerId: string;
    facts: CustomerFacts;
    /** Todos los segmentos ACTIVE que no son el catch-all. */
    segments: CustomerSegmentTrace[];
    /** No cayó en ninguno: le aplica el catch-all. */
    defaultApplies: boolean;
    /** `false` = los cohorts no se pudieron evaluar; solo cuenta la lista manual. */
    evaluatorAvailable: boolean;
}

export interface SegmentLogEntryResponse {
    segmentId: string;
    segmentName: string;
    event: DefinitionLogEvent;
    occurredAt: string;
    /** Tal como quedó asentado. */
    performedBy: string;
    reason: string | null;
    version: number;
}

/** Página de bitácora, del más reciente al más viejo. `nextCursor: null` = no hay más. */
export interface SegmentLogPageResponse {
    entries: SegmentLogEntryResponse[];
    nextCursor: string | null;
}

export interface SegmentLogQuery {
    limit?: number;
    cursor?: string;
}

/** El feed del día: `day` es `YYYY-MM-DD` en hora de Ciudad de México; sin él, el día en curso. */
export interface SegmentLogFeedQuery extends SegmentLogQuery {
    day?: string;
}

export type SegmentationRecountTrigger = 'NIGHTLY' | 'MANUAL';

export type SegmentationRecountStatus = 'RUNNING' | 'FINISHED' | 'FAILED';

export interface SegmentationRecountStartResponse {
    runId: string;
    status: SegmentationRecountStatus;
}

export interface SegmentationRecountRunResponse {
    runId: string;
    trigger: SegmentationRecountTrigger;
    status: SegmentationRecountStatus;
    /** Quién la disparó; `system` en el nocturno. */
    actor: string;
    startedAt: string;
    finishedAt: string | null;
    /** Acreditados evaluados. */
    evaluated: number | null;
    durationMs: number | null;
    /** Corto y sin datos del cliente. */
    error: string | null;
}

/** La más reciente primero, máximo 50. */
export interface SegmentationRecountRunsResponse {
    runs: SegmentationRecountRunResponse[];
}

/** `data` de un 409 `SEGMENT_IN_USE`: lo que hay que reasignar antes de archivar. */
export interface SegmentInUseErrorData {
    storePolicyIds: string[];
    promotionIds: string[];
}

/** `data` de un 409 `SEGMENT_VERSION_CONFLICT`. */
export interface SegmentVersionConflictErrorData {
    segmentId: string;
    expectedVersion: number;
    currentVersion: number;
}

/** `data` de un 422 `SEGMENT_COHORT_UNKNOWN`: los cohorts que no existen o están archivados. */
export interface SegmentCohortUnknownErrorData {
    cohortIds: string[];
}

/** `data` de un 409 `RECOUNT_IN_PROGRESS`: la corrida que sigue abierta. */
export interface RecountInProgressErrorData {
    runId: string;
    startedAt: string;
}

/**
 * Los `code` que devuelve el bloque de segmentos. El `code` ES el contrato con el front; el status
 * solo agrupa por familia. Cada uno tiene su mensaje en pantalla — ver `segments/logic.ts`.
 */
export const SEGMENT_ERROR_CODES = [
    'SEGMENT_NOT_FOUND',
    'SEGMENT_NAME_TAKEN',
    'SEGMENT_VERSION_CONFLICT',
    'DEFAULT_SEGMENT_NOT_EDITABLE',
    'SEGMENT_IN_USE',
    'SEGMENT_COHORT_UNKNOWN',
    'SEGMENT_EMPTY',
    'RECOUNT_IN_PROGRESS',
    'RECOUNT_DISPATCH_FAILED',
    'LOAN_CREDIT_UNAVAILABLE',
] as const;

export type SegmentErrorCode = (typeof SEGMENT_ERROR_CODES)[number];
